import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useTransactions } from '../hooks/useTransactions.js';
import { useCategories } from '../hooks/useCategories.js';
import { useBankAccounts } from '../hooks/useBankAccounts.js';
import { usePeriodRange } from '../hooks/usePeriod.jsx';
import { PROVIDER_LABELS } from '../lib/providers.js';
import TransactionList from '../components/TransactionList.jsx';
import CategoryCombobox from '../components/CategoryCombobox.jsx';
import PeriodSwitcher from '../components/PeriodSwitcher.jsx';
import NotificationsButton from '../components/NotificationsButton.jsx';

export default function TransactionsTabPage() {
  const [searchParams] = useSearchParams();
  const unclassifiedParam = searchParams.get('unclassified') === 'true';

  const { from, to } = usePeriodRange();
  const [page, setPage] = useState(1);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterBankAccount, setFilterBankAccount] = useState('');
  const [unclassifiedOnly, setUnclassifiedOnly] = useState(unclassifiedParam);
  const [searchInput, setSearchInput] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');

  // Debounce : 300ms après la dernière frappe, avec min 2 chars
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchDebounced(searchInput.trim().length >= 2 ? searchInput.trim() : '');
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset page on filter changes
  function handleCategoryChange(v) {
    setFilterCategory(v ?? '');
    setPage(1);
  }
  function handleStatusChange(e) {
    setFilterStatus(e.target.value);
    setPage(1);
  }
  function handleUnclassifiedToggle(e) {
    setUnclassifiedOnly(e.target.checked);
    setPage(1);
  }

  // Re-sync unclassified toggle if URL param changes (e.g. navigation from HomePage alert)
  useEffect(() => {
    setUnclassifiedOnly(unclassifiedParam);
  }, [unclassifiedParam]);

  // CategoryCombobox retourne l'id (UUID) de la catégorie sélectionnée. On
  // utilise category_ids (filtre par UUID) côté backend — le paramètre
  // `category` attend un slug, pas un UUID.
  const { data, isLoading, error } = useTransactions({
    from,
    to,
    category_ids: filterCategory ? [filterCategory] : undefined,
    status: filterStatus || undefined,
    bank_account_id: filterBankAccount || undefined,
    unclassified: unclassifiedOnly || undefined,
    search: searchDebounced || undefined,
    page,
    per_page: 50,
  });

  const { data: catData } = useCategories();
  const { data: bankAccountsData } = useBankAccounts();
  const bankAccounts = Array.isArray(bankAccountsData) ? bankAccountsData : [];
  const categories = Array.isArray(catData) ? catData : catData?.categories ?? catData?.data ?? [];

  const transactions = data?.items ?? (Array.isArray(data) ? data : []);
  const pagination = data
    ? {
        page: data.page ?? 1,
        per_page: data.per_page ?? 50,
        total: data.total ?? transactions.length,
        total_pages: Math.max(
          1,
          Math.ceil((data.total ?? transactions.length) / (data.per_page ?? 50))
        ),
      }
    : null;

  const totalCount = data?.total ?? transactions.length;

  return (
    <div className="space-y-4">
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          {error.message}
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Barre de filtres juste au-dessus du tableau */}
        <div className="border-b border-slate-200 bg-slate-50/50 px-4 py-3 space-y-3">
          {/* Ligne 1 : période + total + notifications */}
          <div className="flex items-center gap-3 flex-wrap">
            <PeriodSwitcher />
            <span className="ml-auto text-xs text-slate-500 tabular-nums">
              {totalCount.toLocaleString('fr-FR')} transaction{totalCount !== 1 ? 's' : ''}
            </span>
            <NotificationsButton />
          </div>

          {/* Ligne 2 : filtres contenu */}
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative w-full sm:w-64">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Rechercher un fournisseur, libellé…"
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500"
                aria-label="Recherche"
              />
            </div>
            <div className="w-full sm:w-64">
              <CategoryCombobox
                value={filterCategory}
                onChange={handleCategoryChange}
                placeholder="Toutes les catégories"
                categories={categories}
              />
            </div>

            <select
              value={filterStatus}
              onChange={handleStatusChange}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500"
              aria-label="Statut Qonto"
            >
              <option value="">Tous les statuts</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="declined">Declined</option>
            </select>

            {bankAccounts.length > 1 && (
              <select
                value={filterBankAccount}
                onChange={(e) => { setFilterBankAccount(e.target.value); setPage(1); }}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500"
                aria-label="Compte bancaire"
              >
                <option value="">Toutes banques</option>
                {bankAccounts.map((ba) => (
                  <option key={ba.id} value={ba.id}>
                    {PROVIDER_LABELS[ba.provider] ?? ba.provider} — {ba.name}
                  </option>
                ))}
              </select>
            )}

            <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-slate-700 pl-1">
              <input
                type="checkbox"
                checked={unclassifiedOnly}
                onChange={handleUnclassifiedToggle}
                className="h-4 w-4 rounded border-slate-300 text-slate-800 focus:ring-slate-500"
              />
              Non classifiées uniquement
            </label>
          </div>
        </div>

        <TransactionList
          transactions={transactions}
          isLoading={isLoading}
          pagination={pagination}
          onPageChange={setPage}
          categories={categories}
        />
      </div>
    </div>
  );
}
